import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Printer,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import warrantyApi from "@/api/warrantyApi";

const WarrantyDocumentModal = ({
  open,
  onOpenChange,
  documentTitle = "Warranty Document",
  claimNumber = "",
  url = "",
  filename = "",
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Store active blob URL in ref for reliable cleanup
  const currentBlobUrlRef = useRef(null);

  const cleanFilename =
    filename ||
    `${documentTitle.replace(/\s+/g, "_")}${claimNumber ? `_${claimNumber}` : ""
    }.pdf`;

  const fetchPdf = React.useCallback(async () => {
    if (!url) return;
    setIsGenerating(true);
    setError(null);

    try {
      const blob = await warrantyApi.fetchPdfBlob(url);
      const newBlobUrl = URL.createObjectURL(blob);

      // Clean up previous blob URL if any
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
      }
      currentBlobUrlRef.current = newBlobUrl;
      setBlobUrl(newBlobUrl);
    } catch (err) {
      console.error("PDF generation error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to generate PDF document";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  }, [url]);

  // Generate PDF automatically whenever the modal opens
  useEffect(() => {
    if (open && url) {
      fetchPdf();
    } else if (!open) {
      // Reset state when modal is closed
      setIsGenerating(false);
      setError(null);
      setBlobUrl(null);
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
        currentBlobUrlRef.current = null;
      }
    }
  }, [open, url, fetchPdf]);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
      }
    };
  }, []);

  const handlePrint = async () => {
    const targetUrl = blobUrl;
    if (!targetUrl) {
      toast.error("Document is not ready yet");
      return;
    }
    setIsPrinting(true);
    try {
      const printWindow = window.open(targetUrl, "_blank");
      if (printWindow) {
        printWindow.focus();
      }
      toast.success("Print preview opened");
      // Do not close the modal automatically after print
    } catch (err) {
      console.error("Print document error:", err);
      toast.error("Failed to open print preview");
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownload = async () => {
    const targetUrl = blobUrl;
    if (!targetUrl) {
      toast.error("Document is not ready yet");
      return;
    }
    setIsDownloading(true);
    try {
      const link = document.createElement("a");
      link.href = targetUrl;
      link.download = cleanFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Document downloaded successfully");
      // Do not close the modal automatically after download
    } catch (err) {
      console.error("Download document error:", err);
      toast.error("Failed to download PDF document");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-xl text-primary shrink-0">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold">
                {documentTitle}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                {isGenerating
                  ? "Generating PDF document..."
                  : error
                    ? "Failed to generate document"
                    : "PDF document ready to print or download"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Document Summary Card */}
          <div className="p-3.5 rounded-xl border bg-muted/30 space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-foreground truncate">
                {documentTitle}
              </span>
              {isGenerating ? (
                <Badge
                  variant="secondary"
                  className="text-[10px] h-5 py-0 gap-1 font-medium"
                >
                  <Loader2 className="h-3 w-3 animate-spin text-primary" />
                  Generating...
                </Badge>
              ) : error ? (
                <Badge
                  variant="destructive"
                  className="text-[10px] h-5 py-0 gap-1 font-medium"
                >
                  <AlertCircle className="h-3 w-3" />
                  Error
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="text-[10px] h-5 py-0 gap-1 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-medium"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Ready
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground pt-1.5 border-t border-border/50">
              <span>Reference</span>
              <span className="font-mono font-medium text-foreground">
                {claimNumber ? `Claim #${claimNumber}` : "Warranty Document"}
              </span>
            </div>
          </div>

          {/* Error display if generation failed */}
          {error && (
            <div className="p-3 rounded-lg border border-destructive/20 bg-destructive/5 text-destructive text-xs space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span className="leading-relaxed">{error}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full text-xs h-8 border-destructive/30 hover:bg-destructive/10 text-destructive gap-1.5"
                onClick={fetchPdf}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry Generation
              </Button>
            </div>
          )}

          {/* Action Choice Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <Button
              type="button"
              variant="outline"
              className="h-16 flex flex-col items-center justify-center gap-1.5 border-border hover:border-primary/50 hover:bg-primary/5 text-foreground transition-all cursor-pointer disabled:cursor-not-allowed"
              onClick={handlePrint}
              disabled={isGenerating || !blobUrl || isPrinting}
            >
              {isPrinting ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              ) : (
                <Printer className="h-5 w-5 text-primary" />
              )}
              <span className="text-xs font-semibold">Print Document</span>
            </Button>

            <Button
              type="button"
              className="h-16 flex flex-col items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground transition-all cursor-pointer disabled:cursor-not-allowed"
              onClick={handleDownload}
              disabled={isGenerating || !blobUrl || isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
              <span className="text-xs font-semibold">Download PDF</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

WarrantyDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  documentTitle: PropTypes.string,
  claimNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  url: PropTypes.string,
  filename: PropTypes.string,
};

export default WarrantyDocumentModal;
